/**
 * Hand-written minimal ABI for `MproLabMarketplace`. Kept here (rather
 * than inside `packages/sdk`) because the frontend is the only consumer
 * — the backend stays out of the marketplace path entirely (M4=B).
 */
export const MARKETPLACE_ABI = [
  // Views
  'function nextListingId() view returns (uint256)',
  'function listings(uint256) view returns (address seller, address nft, uint256 tokenId, uint256 price, uint64 createdAt, bool active, uint16 feeBps)',
  'function activeListingIdOf(address nft, uint256 tokenId) view returns (uint256)',
  'function allowedCollection(address nft) view returns (bool)',
  'function paused() view returns (bool)',
  'function owner() view returns (address)',
  'function feeBps() view returns (uint16)',
  'function feeRecipient() view returns (address)',
  'function MAX_FEE_BPS() view returns (uint16)',
  // What a sale at `price` would pay out at the CURRENT rate — for a seller
  // deciding what to ask.
  'function quote(uint256 price) view returns (uint256 fee, uint256 sellerProceeds)',
  // What an EXISTING listing pays out, at the rate frozen when it was made.
  'function quoteListing(uint256 listingId) view returns (uint256 fee, uint256 sellerProceeds)',
  // Seller
  'function list(address nft, uint256 tokenId, uint256 price) returns (uint256)',
  'function cancel(uint256 listingId)',
  'function updatePrice(uint256 listingId, uint256 newPrice)',
  // Anyone: retires a listing whose seller no longer holds the token, which
  // is what frees the current owner to list it.
  'function clearStale(uint256 listingId)',
  // Buyer. `maxPrice` is the price the buyer saw and agreed to; the call
  // reverts if the seller moved it up in the meantime.
  'function buy(uint256 listingId, uint256 maxPrice)',
  // Events
  'event Listed(uint256 indexed listingId, address indexed seller, address indexed nft, uint256 tokenId, uint256 price)',
  'event Cancelled(uint256 indexed listingId, address indexed seller)',
  'event PriceUpdated(uint256 indexed listingId, uint256 oldPrice, uint256 newPrice, uint16 feeBps)',
  'event Bought(uint256 indexed listingId, address indexed buyer, address indexed seller, address nft, uint256 tokenId, uint256 price, uint256 fee)',
  'event StaleCleared(uint256 indexed listingId, address indexed recordedSeller, address indexed currentOwner, uint8 reason)',
  // ── V2: buy offers, and expiry on both sides ──────────────────────────
  // NOTE the `listings` fragment above stays at the V1 seven-field shape:
  // ethers v5 tolerates trailing return data, so it keeps decoding after
  // the upgrade — while an eight-field fragment against the live V1 would
  // fail on every row and blank the marketplace. readListings upgrades the
  // decode per row via MARKETPLACE_LISTING_V2_FRAGMENT below.
  'function nextOfferId() view returns (uint256)',
  'function offers(uint256) view returns (address buyer, address nft, uint256 unitPrice, uint256 remaining, uint64 createdAt, uint64 expiresAt, bool active, uint16 feeBps)',
  'function makeOffer(address nft, uint256 unitPrice, uint256 quantity, uint64 expiresAt) returns (uint256)',
  // Seller executes; minUnitPrice mirrors buy()'s maxPrice against a buyer
  // lowering the price into a pending accept.
  'function accept(uint256 offerId, uint256 tokenId, uint256 minUnitPrice)',
  'function updateOffer(uint256 offerId, uint256 newUnitPrice)',
  'function updateOfferExpiry(uint256 offerId, uint64 newExpiresAt)',
  'function cancelOffer(uint256 offerId)',
  'function clearStaleOffer(uint256 offerId)',
  'function quoteOffer(uint256 offerId) view returns (uint256 fee, uint256 sellerProceeds)',
  'function list(address nft, uint256 tokenId, uint256 price, uint64 expiresAt) returns (uint256)',
  'function updateListingExpiry(uint256 listingId, uint64 newExpiresAt)',
  'function MIN_EXPIRY_LEAD() view returns (uint64)',
  'event OfferMade(uint256 indexed offerId, address indexed buyer, address indexed nft, uint256 unitPrice, uint256 quantity, uint64 expiresAt)',
  'event OfferAccepted(uint256 indexed offerId, address indexed seller, address indexed buyer, address nft, uint256 tokenId, uint256 unitPrice, uint256 fee, uint256 remaining)',
  'event OfferCancelled(uint256 indexed offerId, address indexed buyer)',
  'event OfferStaleCleared(uint256 indexed offerId, address indexed buyer, uint8 reason)'
] as const

/** The V2 eight-field listing shape, tried first per row by readListings. */
export const MARKETPLACE_LISTING_V2_FRAGMENT = [
  'function listings(uint256) view returns (address seller, address nft, uint256 tokenId, uint256 price, uint64 createdAt, bool active, uint16 feeBps, uint64 expiresAt)'
] as const

/**
 * Multicall3 — canonical address on every major chain including Base
 * mainnet and Base Sepolia. Lets us batch all `listings(id)` reads into
 * a single RPC round-trip.
 */
export const MULTICALL3_ADDRESS =
  '0xcA11bde05977b3631167028862bE2a173976CA11'

export const MULTICALL3_ABI = [
  'function aggregate3(tuple(address target, bool allowFailure, bytes callData)[] calls) payable returns (tuple(bool success, bytes returnData)[])'
] as const

/**
 * Minimal ERC-721 ABI for the approval pre-flight checks, plus the two calls
 * the licence collections add for enumerating a wallet's tokens.
 *
 * The collections are not ERC721Enumerable and their token ids are unix
 * timestamps rather than a sequence, so there is no way to guess them —
 * `getNodesAmountByOwner` + `ownerNodesIds` is the only path to "which
 * licences does this wallet actually hold". (`getNodesIdsByOwner` exists in
 * the newer ABI but not in the deployed contracts.)
 */
export const ERC721_MIN_ABI = [
  'function ownerOf(uint256 tokenId) view returns (address)',
  'function isApprovedForAll(address owner, address operator) view returns (bool)',
  'function getApproved(uint256 tokenId) view returns (address)',
  'function setApprovalForAll(address operator, bool approved)',
  'function transferFrom(address from, address to, uint256 tokenId)',
  'function transferEnabled() view returns (bool)',
  'function getNodesAmountByOwner(address owner) view returns (uint256)',
  'function ownerNodesIds(address owner, uint256 index) view returns (uint256)'
] as const

/**
 * MPRO's master distributor, only for the burn figure.
 *
 * `getBurnAmount` is per sender: a whitelisted address burns nothing, so this
 * answers "what will THIS seller lose", not an average nobody experiences.
 */
export const MASTER_DISTRIBUTOR_MIN_ABI = [
  'function getBurnAmount(address from, uint256 amount) view returns (uint256)',
  'function burnRate() view returns (uint256)'
] as const

/** Minimal ERC-20 ABI for MPRO allowance + approve. */
export const ERC20_MIN_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)'
] as const
